import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Sparkles } from "lucide-react";

const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export default function PostcardShare() {
  const { slug } = useParams();
  const [meta, setMeta] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${backend}/api/postcards/${slug}`);
        setMeta(res.data);
      } catch {
        setErr(true);
      }
    })();
  }, [slug]);

  // Set OG-ish meta tags client-side (best effort for JS-rendering scrapers)
  useEffect(() => {
    if (!meta) return;
    const title = `${meta.name}'s StarGrid Blueprint · ${meta.score} in ${(meta.city || "").replace(/^./, (c) => c.toUpperCase())}`;
    document.title = title;
    const set = (selector, attr, content) => {
      let tag = document.head.querySelector(selector);
      if (!tag) {
        tag = document.createElement("meta");
        const [k, v] = selector.replace(/[\[\]"]/g, "").split("=");
        tag.setAttribute(k, v);
        document.head.appendChild(tag);
      }
      tag.setAttribute(attr, content);
    };
    const img = `${backend}/api/postcards/${slug}.png`;
    const desc = `${meta.zodiac ? meta.zodiac + " · " : ""}Score ${meta.score}/100 resonance in ${meta.city}. Reveal yours on StarGrid.`;
    set('meta[property="og:title"]', "content", title);
    set('meta[property="og:description"]', "content", desc);
    set('meta[property="og:image"]', "content", img);
    set('meta[name="twitter:card"]', "content", "summary_large_image");
    set('meta[name="twitter:image"]', "content", img);
  }, [meta, slug]);

  return (
    <div className="min-h-screen relative bg-[#15111A] text-white">
      <div className="starfield" />
      <div className="noise" />

      <div className="relative z-10 max-w-4xl mx-auto px-6 sm:px-8 pt-16 pb-24">
        <Link to="/" className="flex items-center gap-2 text-[#C8A96E]" data-testid="postcard-brand-link">
          <Sparkles size={18} />
          <span className="font-heading text-xl tracking-tight">StarGrid</span>
        </Link>

        {err && (
          <div className="mt-20 text-center text-[#A89C98]" data-testid="postcard-not-found">
            This postcard has drifted into the void. It may have been deleted or never existed.
          </div>
        )}

        {meta && (
          <>
            <p className="mt-10 text-xs uppercase tracking-[0.3em] text-[#A89C98]" data-testid="postcard-kicker">Blueprint Postcard</p>
            <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter mt-3">
              <span className="text-white">{meta.name}</span>
              <span className="text-[#A89C98]"> read </span>
              <span className="text-[#C8A96E]">{meta.score}</span>
              <span className="text-[#A89C98]"> in </span>
              <span className="text-white capitalize">{meta.city}</span>
            </h1>
            {meta.zodiac && (
              <p className="text-[#A89C98] mt-3 text-sm uppercase tracking-[0.3em]">Sun in {meta.zodiac}</p>
            )}

            <div
              className="mt-10 rounded-2xl overflow-hidden border border-[#C8A96E]/40 shadow-[0_0_60px_rgba(200,169,110,0.15)]"
              data-testid="postcard-hero"
            >
              <picture>
                <source media="(max-width: 639px)" srcSet={`${backend}/api/postcards/${slug}.png?format=portrait`} />
                <img
                  src={`${backend}/api/postcards/${slug}.png`}
                  alt={`${meta.name}'s StarGrid postcard`}
                  className="block w-full h-auto"
                  data-testid="postcard-hero-img"
                />
              </picture>
            </div>

            <div className="mt-12 rounded-2xl p-6 sm:p-8 border border-[#C8A96E]/50 relative overflow-hidden"
                 style={{ background: "linear-gradient(135deg, #1C1622 0%, #4A3356 55%, #6A4F78 100%)" }}
                 data-testid="postcard-cta">
              <div className="absolute -right-8 -top-10 w-52 h-52 rounded-full opacity-40 blur-3xl bg-[#C8A96E]" />
              <div className="relative flex flex-col md:flex-row md:items-center gap-6 justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#C8A96E]">Curious about your own?</div>
                  <div className="font-heading text-2xl sm:text-3xl mt-1">Reveal your blueprint</div>
                  <p className="text-sm text-[#E8DFD3] mt-2 max-w-lg">
                    StarGrid maps your natal chart across Earth — find the cities that resonate with your sign, and the travelers who mirror your compatibility lines.
                  </p>
                </div>
                <Link
                  to="/"
                  data-testid="postcard-cta-btn"
                  className="bg-[#C8A96E] text-[#15111A] hover:bg-[#A98B55] h-14 px-8 uppercase tracking-widest text-sm font-bold shadow-[0_0_28px_rgba(200,169,110,0.45)] flex items-center gap-2 self-start md:self-center"
                >
                  Reveal My Blueprint →
                </Link>
              </div>
            </div>

            <p className="mt-8 text-[10px] uppercase tracking-[0.25em] text-[#A89C98] text-center">
              StarGrid · Astrocartography · one city at a time
            </p>
          </>
        )}
      </div>
    </div>
  );
}
